<template>
    <div v-if="tabs.length" class="thinking-process" :class="{ 'is-completed': isCompleted }">
        <div v-show="!collapsed" class="thinking-body">
            <div class="thinking-tabs">
                <div v-for="(tab, index) in tabs" :key="index" class="thinking-tab"
                    :class="{ active: activeTabIndex === index }" @click="activeTabIndex = index">
                    <span class="tab-dot">●</span>
                    {{ getTabLabel(tab) }}
                </div>
            </div>
            <div ref="contentRef" class="thinking-content">
                <div v-if="activeTab?.type === 'thinking'" class="thinking-text">
                    <!-- <MarkdownPreview :model-value="activeTab.content" editor-id="thinking-md" no-copy /> -->
                    <pre class="w-full break-all whitespace-pre-line" v-html="activeTab.content"></pre>
                </div>
                <div v-else-if="activeTab?.type === 'tool'" class="tool-call-info">
                    <!-- <MarkdownPreview v-if="activeTab.content" :model-value="activeTab.content"
                        editor-id="thinking-tool-md" no-copy /> -->
                    <pre class="w-full break-all whitespace-pre-line" v-html="activeTab.content"></pre>
                </div>
                <span v-if="!isCompleted && activeTabIndex === tabs.length - 1" class="cursor-blink">|</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
// import MarkdownPreview from '@/components/MarkdownPreview.vue';

export interface ThinkingTab {
    type: 'thinking' | 'tool';
    label: string;
    toolName?: string;
    content: string;
}

export type ThinkingStatus = 'idle' | 'thinking' | 'completed' | 'interrupted';

const props = withDefaults(defineProps<{
    tabs: ThinkingTab[];
    activeTabIndex: number;
    status: ThinkingStatus;
    collapsed?: boolean;
}>(), {
    collapsed: false,
});

const emit = defineEmits<{
    (e: 'update:activeTabIndex', index: number): void;
    (e: 'update:collapsed', value: boolean): void;
}>();

const { t, te } = useI18n();

const getTabLabel = (tab: ThinkingTab) => {
    if (tab.type === 'thinking') return t('floatBox.thinkingTabLabel');
    if (tab.toolName) {
        const key = `floatBox.toolNames.${tab.toolName}`;
        return te(key) ? t(key) : tab.toolName;
    }
    return tab.label;
};
const contentRef = ref<HTMLElement | null>(null);

const activeTabIndex = computed({
    get: () => props.activeTabIndex,
    set: val => emit('update:activeTabIndex', val),
});

const activeTab = computed(() => props.tabs[activeTabIndex.value]);
const isCompleted = computed(() => props.status === 'completed' || props.status === 'interrupted');

const toggleCollapse = () => {
    emit('update:collapsed', !props.collapsed);
};

const scrollContentToBottom = () => {
    nextTick(() => {
        const el = contentRef.value;
        if (el) el.scrollTop = el.scrollHeight;
    });
};

watch(
    () => activeTab.value?.content,
    () => {
        if (!props.collapsed && activeTabIndex.value === props.tabs.length - 1) {
            scrollContentToBottom();
        }
    }
);

watch(
    () => props.tabs.length,
    () => {
        activeTabIndex.value = props.tabs.length - 1;
    },
);

watch(isCompleted, val => {
    if (val) {
        emit('update:collapsed', true);
    }
});
</script>

<style lang="scss" scoped>
.thinking-process {
    margin: 4px 0 8px;
    font-size: 12px;
    color: #8f91aa;
    line-height: 1.5;
    word-break: break-all;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #e8e8ec;
}

.thinking-tabs {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    overflow-x: auto;
    padding-bottom: 12px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.thinking-tab {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 18px;
    padding: 4px 8px;
    border-radius: 10px;
    white-space: nowrap;
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.2s;
    background: #f4f4f6;
    color: #666666;
    border: none;

    .tab-dot {
        font-size: 8px;
        color: #4caf50;
    }

    &.active {
        background: #eaf6eb;
        color: #4caf50;
        border: none;
    }

    &:hover:not(.active) {
        background: #edeef0;
        color: rgba(8, 8, 8, 0.75);
    }
}

.thinking-content {
    max-height: 90px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #dadada transparent;
    font-size: 12px;
    color: #8f91aa;
    line-height: 1.5;
    word-break: break-all;
}

.thinking-text {
    word-break: break-word;
}

.cursor-blink {
    color: #38a169;
    animation: blink 0.8s infinite;
}

@keyframes blink {

    0%,
    50% {
        opacity: 1;
    }

    51%,
    100% {
        opacity: 0;
    }
}
</style>
